import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";
import MentionTextarea from "@/components/MentionTextarea";
import GiftPicker from "@/components/GiftPicker";
import MembershipBadges from "@/components/MembershipBadges";

/**
 * CommentsDrawer — A slide-up panel that shows comments for a track without
 * navigating away (so the audio player stays mounted and keeps playing).
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - submissionId: track id (required)
 *   - currentUser: the logged-in user (or null)
 *   - trackTitle: optional string (for the header)
 */
const CommentsDrawer = ({ open, onClose, submissionId, currentUser, trackTitle }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!submissionId) return;
    setLoading(true);
    try {
      const r = await api.get(`/track/${submissionId}/comments`);
      setComments(r.data?.comments || []);
    } catch (e) {
      // silent — keeps drawer usable even when listing fails
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    if (open) fetchComments();
  }, [open, fetchComments]);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!currentUser) {
      toast.error("Please sign in to comment");
      return;
    }
    const body = text.trim();
    if (!body) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/track/${submissionId}/comments`, {
        content: body,
        mention_user_ids: mentions,
      });
      setComments((prev) => [...prev, r.data.comment]);
      setText("");
      setMentions([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center"
      data-testid="comments-drawer"
      role="dialog"
      aria-label="Comments"
    >
      {/* Backdrop — tapping closes; audio player is OUTSIDE this tree so it keeps playing */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
        data-testid="comments-drawer-backdrop"
      />
      <div
        className="relative w-full sm:w-[540px] max-h-[85vh] sm:max-h-[80vh] bg-[#0f0c18] border-t sm:border border-purple-500/30 sm:rounded-xl flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-purple-300">Comments</p>
            <p className="text-sm text-white font-semibold truncate">
              {trackTitle || "This track"} · {comments.length}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl leading-none px-2"
            aria-label="Close comments"
            data-testid="close-comments-drawer"
          >
            ×
          </button>
        </div>

        {/* Comments list */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading ? (
            <p className="text-gray-500 text-sm text-center py-6">Loading comments…</p>
          ) : comments.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">Be the first to comment.</p>
          ) : (
            comments.map((c) => (
              <div
                key={c.comment_id}
                className="flex gap-2"
                data-testid={`drawer-comment-${c.comment_id}`}
              >
                <img
                  src={getProfilePhotoUrl({
                    profile_photo: c.user_profile_photo,
                    picture: c.user_picture,
                  })}
                  alt={c.user_name}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0 bg-gray-900/60 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                    <p className="text-xs text-purple-300 font-semibold truncate min-w-0">
                      {c.user_name || "Anonymous"}
                    </p>
                    <MembershipBadges
                      user={{
                        role: c.user_role,
                        badges: c.user_badges,
                        founder_number: c.user_founder_number,
                        membership_tier: c.user_membership_tier,
                      }}
                      size="sm"
                      testIdPrefix={`comment-membership-${c.comment_id}`}
                    />
                  </div>
                  <p className="text-sm text-white whitespace-pre-wrap break-words">
                    {c.content}
                  </p>
                  {/* Gift on comment — small icon-style picker */}
                  {currentUser && c.user_id !== currentUser.user_id && (
                    <div className="mt-1.5">
                      <GiftPicker
                        targetType="comment"
                        targetId={c.comment_id}
                        recipientId={c.user_id}
                        currentUserId={currentUser.user_id}
                        currentUserRole={currentUser.role}
                        compact
                      />
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Composer */}
        {currentUser ? (
          <div className="border-t border-gray-800 p-3 flex flex-col gap-2">
            <MentionTextarea
              value={text}
              onChange={setText}
              onMentionsChange={setMentions}
              placeholder="Add a comment… type @ to tag someone"
              maxLength={1000}
              rows={2}
              className="bg-black/50 border-gray-700 text-white min-h-[56px]"
              testIdPrefix="drawer-comment"
            />
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500">{text.length}/1000</span>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !text.trim()}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                data-testid="drawer-submit-comment"
              >
                {submitting ? "Posting…" : "Post"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-t border-gray-800 p-3 text-center text-sm text-gray-400">
            Sign in to join the conversation.
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentsDrawer;
